<template>
    <span 
        class="w-auto"
        :class="[
            text == '' ? 'text-center' : text
        ]">
        <span v-if="!$debugStore.TranslateMode">{{ Translate(value) }}</span>
        <span v-else class="cursor-pointer !text-red-500 hover:text-red-600 bg-red-100" @click.stop="openTranslateModal">
            {{ Translate(value) }}
        </span>
        
        <!-- Translation Modal -->
        <Modal :show="showModal" @close="closeTranslationModal" label="$PageEditor$:translations" :CloseButton="false">
            <div class="p-4">
                <div v-if="loadingTranslations" class="text-center py-8">
                    <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p class="text-gray-600">Loading translations...</p>
                </div>
                <div v-else-if="translationData" class="space-y-4">
                    <div class="bg-gray-50 p-3 rounded">
                        <h3 class="font-semibold text-gray-900 mb-2">Translation Key:</h3>
                        <code class="bg-white px-2 py-1 rounded border text-sm">{{ translationData.link || value }}</code>
                    </div>
                    <div v-if="hasTranslations">
                        <h3 class="font-semibold text-gray-900 mb-3">Edit Translations:</h3>
                        <div class="space-y-3">
                            <div v-for="(translation, langCode) in editableTranslations" :key="langCode" 
                                 class="border rounded p-4 bg-white">
                                <div class="flex justify-between items-center mb-3">
                                    <label :for="'translation-' + langCode" class="font-medium text-blue-600 uppercase">
                                        {{ langCode }}
                                    </label>
                                    <span v-if="translation" class="text-xs text-green-600 bg-green-100 px-2 py-1 rounded">Has Translation</span>
                                    <span v-else class="text-xs text-orange-600 bg-orange-100 px-2 py-1 rounded">Empty</span>
                                </div>
                                <input 
                                    :id="'translation-' + langCode"
                                    v-model="editableTranslations[langCode]"
                                    type="text" 
                                    :placeholder="'Enter translation for ' + langCode.toUpperCase()"
                                    class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                    :class="{ 'border-orange-300 bg-orange-50': !translation }"
                                />
                            </div>
                        </div>
                        <div class="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
                            <button 
                                @click="closeTranslationModal"
                                class="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors">
                                Cancel
                            </button>
                            <button 
                                @click="saveTranslations"
                                class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors">
                                Save Changes
                            </button>
                        </div>
                    </div>
                    <div v-else class="text-center py-4 text-gray-500">
                        No translations found for this key.
                    </div>
                </div>
                <div v-else class="text-center py-4 text-gray-500">
                    No translation data available.
                </div>
            </div>
        </Modal>
    </span>
</template>

<script>
import Modal from "@/components/Blocks/Modal.vue"
import FetchData from '@/utils/FetchData';

export default{
    name: 'Translate',
    data() {
        return {
            showModal: false,
            translationData: null,
            loadingTranslations: false,
            editableTranslations: {}
        }
    },
    components: {
        Modal
    },
    props: {
        value: {
            type: String,
            required: true
        },
        text: {
            type: String,
            default: ''
        }
    },
    computed: {
        filteredTranslations() {
            if (!this.translationData) return {};
            
            // Remove 'link' property and return only language translations
            return Object.fromEntries(
                Object.entries(this.translationData).filter(([key]) => key !== 'link')
            );
        },
        hasTranslations() {
            return Object.keys(this.editableTranslations).length > 0;
        }
    },
    methods: {
        Translate(key) {
            if (!key) return '';
            return this.$languageStore.getTranslation(key);
        },
        async openTranslateModal() {
            if(!this.$debugStore.TranslateMode) return;
            this.showModal = true;
            this.loadingTranslations = true;
            
            try {
                this.translationData = await this.LoadTranslationsForValue(this.value);
                // Initialize editable translations
                this.editableTranslations = { ...this.filteredTranslations };
            } catch (error) {
                console.error("Error loading translations:", error);
                this.translationData = null;
                this.editableTranslations = {};
            } finally {
                this.loadingTranslations = false;
            }
        },
        closeTranslationModal() {
            this.showModal = false;
            this.translationData = null;
            this.loadingTranslations = false;
            this.editableTranslations = {};
        },
        saveTranslations() {
            const translationSubmission = {
                translationKey: this.translationData.link || this.value,
                changes: this.getTranslationChanges()
            };
            
            this.SaveChangesForTranslations(translationSubmission.translationKey, translationSubmission.changes)
                .then(() => {
                    // Reload translations to update the interface
                    this.$languageStore.ReloadTranslations();
                })
                .catch(error => {
                    console.error('Error saving translation:', error);
                });
            
            // Close modal after saving
            this.closeTranslationModal();
        },
        getTranslationChanges() {
            const changes = {};
            const original = this.filteredTranslations;
            
            Object.keys(this.editableTranslations).forEach(langCode => {
                const originalValue = original[langCode] || '';
                const newValue = this.editableTranslations[langCode] || '';
                
                if (originalValue !== newValue) {
                    changes[langCode] = {
                        from: originalValue,
                        to: newValue
                    };
                }
            });
            
            return changes;
        },
        async LoadTranslationsForValue(value){
            const route = "Translations/" + value;
            try{
                const response = await FetchData({}, route, "GET");
                return response;
            } catch (error) {
                console.error("Error loading translations:", error);
                throw error;
            }
        },
        async SaveChangesForTranslations(key, values){
            const route = "Translations/" + key;
            const formattedValues = Object.keys(values).reduce((acc, lang) => {
                acc[lang] = values[lang].to;
                return acc;
            }, {});

            try {
                const response = await FetchData(formattedValues, route, "PUT");
                return response;
            } catch (error) {
                console.error("Error saving changes:", error);
                throw error;
            } finally {
                await this.$languageStore.ReloadTranslations();
            }
        }
    }
}
</script>